/**
 * /view/survey/:token (tracker item 69): rate a finished visit from the
 * emailed link, outside the app's sign-in and nav (no chrome). Mobile first:
 * large star buttons, one comment box, one Submit.
 *
 * `?rating=N` (the one-click links in the email) only pre-selects N: nothing
 * is sent until the customer presses Submit, so a mail scanner that opens the
 * link cannot answer for them. Every unusable link shows the same "not
 * available" page; an answered or expired link says so. The comment is plain
 * text that is never shown back on this page.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { createAPIClient, ISurveysApi } from '../../api';
import * as types from '../../api/types';
import { StarDisplay, StarPicker } from '../../components/StarRating';
import { inputClass, labelClass, primaryButtonClass } from '../../components/formStyles';
import { publicAnswerOutcome, ratingLabel, ratingParam, surveyDate } from '../surveys/surveyUi';
import { Unavailable } from './PublicDocumentPages';

type Phase = 'form' | 'thanks' | 'answered' | 'expired' | 'unavailable';

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  useEffect(() => {
    const previous = document.title;
    document.title = title;
    return () => { document.title = previous; };
  }, [title]);
  return (
    <main className="min-h-screen bg-gray-100 px-4 py-6 sm:py-12">
      <div className="mx-auto w-full max-w-lg rounded-lg bg-white p-5 shadow sm:p-8">{children}</div>
    </main>
  );
}

export function PublicSurveyPage() {
  const { token = '' } = useParams<{ token: string }>();
  const [search] = useSearchParams();
  const api = useMemo<ISurveysApi>(() => createAPIClient(), []);
  const [state, setState] = useState<{ survey: types.PublicSurvey | null; loading: boolean }>({ survey: null, loading: true });
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>('form');
  const preselected = ratingParam(search.get('rating'));

  useEffect(() => {
    let active = true;
    api.getPublicSurvey(token)
      .then((survey) => {
        if (!active) return;
        setState({ survey, loading: false });
        setPhase(survey.status === 'ANSWERED' ? 'answered' : survey.status === 'EXPIRED' ? 'expired' : 'form');
        setRating((current) => current ?? ratingParam(search.get('rating'), survey.scale.min, survey.scale.max));
      })
      .catch(() => { if (active) setState({ survey: null, loading: false }); });
    return () => { active = false; };
    // search is read once for the pre-selection; the link never changes while the page is open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, token]);

  const survey = state.survey;
  if (!survey || phase === 'unavailable') return <Unavailable loading={state.loading} />;
  const company = survey.companyName;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (rating === null || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.submitPublicSurvey(token, { rating, comment: comment.trim() || null });
      setPhase('thanks');
    } catch (cause) {
      const outcome = publicAnswerOutcome(cause);
      if (outcome === 'error') setError('We could not send your answer. Please try again.');
      else setPhase(outcome);
    } finally {
      setBusy(false);
    }
  }

  if (phase === 'thanks') {
    return (
      <Shell title={`Thank you - ${company}`}>
        <div role="status" className="text-center">
          <h1 className="mt-2 text-xl font-bold text-gray-900">Thank you!</h1>
          <p className="mt-2 text-gray-700">Your feedback was sent to {company}. We appreciate you taking a moment.</p>
          {rating !== null && <p className="mt-3"><StarDisplay rating={rating} className="text-2xl" /></p>}
        </div>
      </Shell>
    );
  }

  if (phase === 'answered') {
    return (
      <Shell title={`Survey answered - ${company}`}>
        <div role="status" className="text-center">
          <h1 className="text-xl font-bold text-gray-900">Thanks, we already have your answer</h1>
          <p className="mt-2 text-gray-700">This survey was answered and can only be answered once.</p>
          {survey.rating !== null && <p className="mt-3"><StarDisplay rating={survey.rating} className="text-2xl" /></p>}
        </div>
      </Shell>
    );
  }

  if (phase === 'expired') {
    return (
      <Shell title={`Survey expired - ${company}`}>
        <div role="status" className="text-center">
          <h1 className="text-xl font-bold text-gray-900">This survey link has expired</h1>
          <p className="mt-2 text-gray-700">If you would still like to tell {company} how your visit went, just reply to their email or give them a call.</p>
        </div>
      </Shell>
    );
  }

  const technician = survey.technicianFirstName;
  const commentId = 'survey-comment';
  return (
    <Shell title={`How was your visit? - ${company}`}>
      <form onSubmit={submit} noValidate>
        <p className="text-sm font-semibold text-gray-600 break-words">{company}</p>
        <h1 className="mt-1 text-2xl font-bold text-gray-900">How was your visit?</h1>
        <p className="mt-2 text-gray-700 break-words">
          Hello {survey.customerFirstName}, {technician ? `${technician} from ` : ''}{company} completed your service
          {survey.jobDate ? ` on ${surveyDate(survey.jobDate)}` : ''}: {survey.serviceList}.
        </p>

        <div className="mt-5">
          <StarPicker value={rating} onChange={setRating} labels={survey.scale.labels} disabled={busy} legend="Rate your visit from 1 to 5" />
          <p className="mt-2 text-sm text-gray-600" aria-live="polite">
            {rating === null
              ? 'Choose a rating, then press Send.'
              : preselected === rating
                ? `You chose ${rating}, ${ratingLabel(rating, survey.scale.labels)}. Press Send to submit it, or choose another.`
                : `You chose ${rating}, ${ratingLabel(rating, survey.scale.labels)}.`}
          </p>
        </div>

        <div className="mt-5">
          <label htmlFor={commentId} className={labelClass}>{survey.commentPrompt}</label>
          <textarea
            id={commentId}
            className={inputClass}
            rows={4}
            maxLength={survey.commentMaxLength}
            value={comment}
            disabled={busy}
            onChange={(e) => setComment(e.target.value)}
          />
          <p className="mt-1 text-right text-xs text-gray-500" aria-hidden="true">{comment.length} / {survey.commentMaxLength}</p>
        </div>

        {error && <p role="alert" className="mt-3 rounded bg-red-50 p-3 text-sm text-red-800">{error}</p>}
        <button type="submit" className={`${primaryButtonClass} mt-4 w-full min-h-[48px] text-base`} disabled={busy || rating === null}>
          {busy ? 'Sending…' : 'Send'}
        </button>
      </form>
    </Shell>
  );
}
